import { Badge as CompassBadge } from "@circleco/compass/components/Badge";
import { allowedClassesFor } from "@circleco/compass/helpers/allowedClassesFor";
import omit from "lodash/omit";
import type { ComponentPropsWithRef, CSSProperties } from "react";

import type { BadgeV2Props as UIKitBadgeV2Props } from "@/react/components/shared/uikit/BadgeV2";
import { BadgeV2 } from "@/react/components/shared/uikit/BadgeV2";
import { legacyClassNames } from "@/react/helpers/cssAnchorHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

// eslint-disable-next-line no-barrel-files/no-barrel-files -- This is a compatibility layer that re-exports types for SettingsApp
export type { BadgeV2Props as BadgeProps } from "@/react/components/shared/uikit/BadgeV2";

type CompassBadgeProps = ComponentPropsWithRef<typeof CompassBadge>;

type ExtendedBadgeV2Props = UIKitBadgeV2Props & {
  variant?: CompassBadgeProps["variant"];
  textClassName?: string;
};

const mapVariant = (
  inverted?: boolean,
  explicitVariant?: CompassBadgeProps["variant"],
): CompassBadgeProps["variant"] | undefined => {
  if (explicitVariant) {
    return explicitVariant;
  }

  if (inverted) {
    return "primary";
  }
  return "secondary";
};

export const Badge = (props: ExtendedBadgeV2Props) => {
  const isCircleV4 = useIsCircleV4FeatureEnabled("circle_v4_badges_enabled");

  if (isCircleV4) {
    const {
      emoji,
      label,
      inverted: isInverted,
      variant: explicitVariant,
      styles,
      className,
      textClassName,
      dataTestId,
      ...restProps
    } = props;

    // Fall back to BadgeV2 if emoji is provided (Compass Badge doesn't support emoji)
    if (emoji || !label) {
      return <BadgeV2 {...props} />;
    }

    const compassVariant = mapVariant(isInverted, explicitVariant);

    const compassRestProps = omit(restProps, [
      "square",
      "tooltipCursor",
      "tooltipText",
      "truncate",
      "truncateLength",
      "labelClassName",
      "textSizeClassName",
    ]);

    const compassProps: CompassBadgeProps = {
      ...compassRestProps,
      variant: compassVariant,
      label,
      className: className ? allowedClassesFor("Badge", className) : "",
      textClassName: textClassName
        ? allowedClassesFor("Badge", textClassName)
        : "",
      style: styles as CSSProperties,
    };

    return (
      <CompassBadge
        {...compassProps}
        data-testid={classNames(legacyClassNames("member-tag"), dataTestId)}
      />
    );
  }

  return <BadgeV2 {...props} />;
};
